import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Shows whether the event calendar's Facebook token works, and lets you (re)connect Facebook.
// Connecting is a normal Facebook login; the server turns it into a Page token that
// doesn't expire and saves it (see FacebookAuthController.cs).
const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '');

const FacebookConnection = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState(null); // message after coming back from Facebook

    // Coming back from Facebook: /admin?facebook=connected|error&message=...
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const outcome = params.get('facebook');
        if (outcome) {
            setResult({ ok: outcome === 'connected', message: params.get('message') || '' });
            navigate(location.pathname, { replace: true }); // tidy the address bar
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async (method = 'GET') => {
        const url = method === 'POST' ? '/api/FacebookAuth/check' : '/api/FacebookAuth/status';
        const res = await fetch(url, { method });
        if (!res.ok) throw new Error(`Status ${res.status}`);
        setStatus(await res.json());
    };

    useEffect(() => {
        load()
            .catch((err) => setResult({ ok: false, message: `Could not load the Facebook status (${err.message}).` }))
            .finally(() => setLoading(false));
    }, []);

    const checkNow = async () => {
        setChecking(true);
        try {
            await load('POST');
        } catch (err) {
            setResult({ ok: false, message: `Check failed (${err.message}).` });
        } finally {
            setChecking(false);
        }
    };

    // A full-page visit: the server sends you to Facebook and Facebook sends you back here
    const connect = () => {
        window.location.href = '/api/FacebookAuth/login';
    };

    const dataAccessSoon =
        status?.dataAccessExpiresAt &&
        new Date(status.dataAccessExpiresAt) - new Date() < 14 * 24 * 60 * 60 * 1000;

    return (
        <div className="facebook-events-update">
            <h3>Facebook Connection</h3>

            {result && (
                <p style={{ color: result.ok ? 'green' : 'red' }}>{result.message}</p>
            )}

            {loading ? (
                <p>Checking the Facebook connection…</p>
            ) : status && (
                <>
                    {!status.configured && (
                        <p style={{ color: 'red' }}>
                            Add <code>Facebook:AppId</code> and <code>Facebook:AppSecret</code> to
                            appsettings.Secrets.json (from developers.facebook.com, your app, App settings, Basic),
                            then restart the site.
                        </p>
                    )}

                    {status.isValid ? (
                        <p style={{ color: 'green' }}>
                            Working{status.pageName ? ` (${status.pageName})` : ''}.{' '}
                            {status.neverExpires
                                ? 'The token does not expire.'
                                : `The token expires on ${formatDate(status.expiresAt)}.`}
                        </p>
                    ) : (
                        <p style={{ color: 'red' }}>Not working: {status.error}</p>
                    )}

                    {status.note && <p>{status.note}</p>}

                    {dataAccessSoon && (
                        <p style={{ color: '#b36b00' }}>
                            Facebook asks you to log in again by {formatDate(status.dataAccessExpiresAt)}.
                            Click Reconnect Facebook before then.
                        </p>
                    )}

                    <p style={{ fontSize: '0.85em', opacity: 0.75 }}>
                        Token from: {status.source === 'connected' ? 'Connect Facebook' : 'appsettings.Secrets.json'}
                        {status.checkedAt ? `, last checked ${new Date(status.checkedAt).toLocaleString()}` : ''}
                    </p>

                    <button onClick={connect} disabled={!status.configured}>
                        {status.source === 'connected' && status.isValid ? 'Reconnect Facebook' : 'Connect Facebook'}
                    </button>{' '}
                    <button onClick={checkNow} disabled={checking}>
                        {checking ? 'Checking…' : 'Check now'}
                    </button>

                    <details style={{ marginTop: '0.75rem', fontSize: '0.85em' }}>
                        <summary>Facebook app setup (one time)</summary>
                        <p>
                            In your app on developers.facebook.com, add the Facebook Login product and put this
                            address in Valid OAuth Redirect URIs:
                        </p>
                        <code style={{ wordBreak: 'break-all' }}>{status.redirectUri}</code>
                    </details>
                </>
            )}
        </div>
    );
};

export default FacebookConnection;
